import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { formatDistanceToNow } from 'date-fns';

export interface PatientQuestionHistoryItem {
  id: string;
  nutritionistName: string;
  question: string;
  status: 'PENDING' | 'ANSWERED';
  askedAt: string;
  answeredAt?: string;
  answer?: string;
}

interface PatientQuestionHistorySectionProps {
  questions: PatientQuestionHistoryItem[];
}

export const PatientQuestionHistorySection = ({ questions }: PatientQuestionHistorySectionProps) => {
  if (questions.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <QuestionAnswerIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography color="text.secondary">
          No questions asked yet. Use the form above to ask your first question!
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {questions.map((question) => (
        <Card key={question.id} variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    icon={question.status === 'ANSWERED' ? <CheckCircleIcon /> : <AccessTimeIcon />}
                    label={question.status === 'ANSWERED' ? 'Answered' : 'Pending'}
                    color={question.status === 'ANSWERED' ? 'success' : 'warning'}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Asked {formatDistanceToNow(new Date(question.askedAt), { addSuffix: true })}
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  To: {question.nutritionistName}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Your Question:
              </Typography>
              <Typography variant="body1">
                {question.question}
              </Typography>
            </Box>

            {question.status === 'ANSWERED' && question.answer && (
              <Box sx={{ bgcolor: 'primary.50', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="primary.main" gutterBottom fontWeight={600}>
                  Answer from {question.nutritionistName}:
                </Typography>
                <Typography variant="body2">
                  {question.answer}
                </Typography>
                {question.answeredAt && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Answered {formatDistanceToNow(new Date(question.answeredAt), { addSuffix: true })}
                  </Typography>
                )}
              </Box>
            )}

            {question.status === 'PENDING' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Your nutritionist will respond within 24-48 hours
              </Alert>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};